import { useHistory } from "react-router-dom";
import { Badge, Card, SkeletonLoader, Text } from "@merge-api/merge-javascript-shared";
import { ChevronRight } from "lucide-react";
import { ResolvedBlueprintIdentifier } from "../utils/resolveBlueprintIdentifier";
import {
  getBlueprintEditorPath,
  getBlueprintsPath,
  navigateHelper,
} from "../../../../router/RouterUtils";

type Props = {
  resolved: ResolvedBlueprintIdentifier | undefined;
  isNotFound: boolean;
  isLoading: boolean;
};

const publishStateColor = (publishState: string | null) => {
  if (publishState === "PUBLISHED") return "green";
  if (publishState === "STAGED") return "blue";
  if (publishState === "DRAFT") return "yellow";
  return "gray";
};

const publishStateLabel = (publishState: string | null) => {
  if (!publishState) return "Unknown";
  return publishState.charAt(0) + publishState.slice(1).toLowerCase();
};

const BlueprintSearchResultCard = ({ resolved, isNotFound, isLoading }: Props) => {
  // hooks
  const history = useHistory();

  // event handlers
  const onClick = (event: React.MouseEvent<HTMLButtonElement | HTMLDivElement>) => {
    if (!resolved) return;

    const destination = resolved.blueprint_version_id
      ? getBlueprintEditorPath(resolved.integration_id, resolved.blueprint_version_id)
      : getBlueprintsPath(resolved.integration_id);

    navigateHelper(history, destination, event.metaKey || event.ctrlKey);
  };

  // render
  // Fixed width (rather than sizing to content) in every state, so the card never resizes
  // between loading, not-found, and found -- deliberately a bit wider than the search bar above
  // it rather than matching it exactly.
  if (isLoading) {
    return (
      <Card className="flex w-full max-w-2xl flex-col gap-3 p-6">
        <SkeletonLoader fullWidth height={20} borderRadius={8} />
        <SkeletonLoader width={200} height={16} borderRadius={8} />
      </Card>
    );
  }

  if (isNotFound || !resolved) {
    return (
      <Card className="flex w-full max-w-2xl flex-col gap-2 p-6">
        <Text variant="h6">Nothing matches that ID</Text>
        <Text variant="sm" className="text-gray-40">
          Paste an Integration, Blueprint or Blueprint version ID, or search by Integration name
        </Text>
      </Card>
    );
  }

  const isIntegration = resolved.kind === "INTEGRATION";

  return (
    /* The shared Card only wires onClick when type="button", so the handler lives on a wrapper */
    <div className="w-full max-w-2xl cursor-pointer" onClick={onClick}>
      <Card className="flex w-full flex-row items-center gap-4 p-6 hover:bg-gray-0">
        <div className="flex min-w-0 flex-1 flex-col">
          <Text variant="h6">
            {isIntegration ? resolved.integration_name : resolved.blueprint_name}
          </Text>
          <Text variant="sm" className="text-gray-40">
            {isIntegration ? "Integration Blueprints" : resolved.integration_name}
          </Text>
        </div>

        {!isIntegration && (
          <Badge size="sm" color={publishStateColor(resolved.blueprint_version_publish_state)}>
            {publishStateLabel(resolved.blueprint_version_publish_state)}
          </Badge>
        )}

        {/* A version ID already names itself; only the other kinds need saying which page opens */}
        {resolved.kind !== "BLUEPRINT_VERSION" && (
          <Badge size="sm" color="gray">
            {isIntegration ? "Integration" : "Latest version"}
          </Badge>
        )}

        <ChevronRight size={16} className="text-gray-40" />
      </Card>
    </div>
  );
};

export default BlueprintSearchResultCard;
